import React, { memo } from "react";
import "../../css/FullEvent/FullEventInputForm.css";
import "../../css/GlobalStyles.css";
import {
  AccountData,
  CustomTicketV2,
  Event,
  PromoCode,
} from "@markit/common.types";
import { isEventTicketsPaid } from "@markit/common.utils";
import { Colors } from "../../utils/colors";
import { useTheme } from "../../hooks/useTheme";
import { CircularProgress } from "@mui/material";
import { FullEventPaymentSummary } from "./FullEventPaymentSummary";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";

type FullEventPaymentProps = {
  event: Event;
  host: AccountData;
  ticketForRedemption: CustomTicketV2 | undefined;
  setTicketForRedemption: (
    ticketForRedemption: CustomTicketV2 | undefined
  ) => void;
  promoCode: PromoCode | undefined;
  tixId: string;
  selectedCustomTickets: CustomTicketV2[];
  setSelectedCustomTickets: (selectedCustomTickets: CustomTicketV2[]) => void;
  ticketQuantities: Map<string, number>;
  setTicketQuantities: (ticketQuantities: Map<string, number>) => void;
  namedPrices: Map<string, number>;
  baseTicketPriceCents: number;
  ticketFeesCents: number;
  totalAmountCents: number;
  discountedTicketPriceCents: number;
  numTicketsUserInGroups: number[];
  disabled: boolean;
  verified: boolean;
  paymentProcessing: boolean;
  updatePromoCode: (promoCode: PromoCode | undefined) => void;
  updateTixId: (tixId: string) => void;
  setIsRedeemFreePromoCode: (isRedeemFreePromoCode: boolean) => void;
  paymentOrRsvpElement: React.ReactNode;
  isDesktop: boolean;
  showTipSection: boolean;
  tipAmountCents: number;
  setTipAmountCents: (tipAmountCents: number) => void;
};

export const FullEventPayment = memo(function FullEventPaymentFn(
  props: FullEventPaymentProps
) {
  const {
    event,
    host,
    ticketForRedemption,
    setTicketForRedemption,
    promoCode,
    tixId,
    selectedCustomTickets,
    setSelectedCustomTickets,
    ticketQuantities,
    setTicketQuantities,
    namedPrices,
    baseTicketPriceCents,
    ticketFeesCents,
    totalAmountCents,
    discountedTicketPriceCents,
    numTicketsUserInGroups,
    disabled,
    verified,
    paymentProcessing,
    updatePromoCode,
    updateTixId,
    setIsRedeemFreePromoCode,
    paymentOrRsvpElement,
    isDesktop,
    showTipSection,
    tipAmountCents,
    setTipAmountCents,
  } = props;
  const { theme } = useTheme();

  const styles = {
    mobilePaymentContainer: {
      padding: 14,
      borderRadius: 20,
    },
  };

  if (!isEventTicketsPaid(event)) {
    return null;
  }

  if (disabled && verified && !paymentProcessing) {
    return (
      <div
        className="Centering"
        style={{
          padding: "40px 14px",
        }}
      >
        <CircularProgress size={24} style={theme.PrimaryText} />
      </div>
    );
  }

  return (
    <div
      className="ColumnNormal"
      style={{
        opacity: disabled ? 0.4 : 1,
        pointerEvents: disabled ? "none" : "all",
        gap: 14,
      }}
    >
      <div className="ColumnNormal" style={{ gap: 7 }}>
        <h4 className="NameAndNumber" style={theme.PrimaryText}>
          Payment
        </h4>
        <span style={{ color: Colors.GRAY2, fontSize: 12 }}>
          {verified
            ? "Enter the following info to complete your purchase"
            : "Available after info entered"}
        </span>
      </div>
      {!isDesktop && verified ? (
        <StandardBorderedContainer
          containerStyles={{
            ...styles.mobilePaymentContainer,
            ...theme.NewPrimaryBG,
          }}
        >
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <FullEventPaymentSummary
              event={event}
              host={host}
              ticketForRedemption={ticketForRedemption}
              setTicketForRedemption={setTicketForRedemption}
              promoCode={promoCode}
              tixId={tixId}
              selectedCustomTickets={selectedCustomTickets}
              setSelectedCustomTickets={setSelectedCustomTickets}
              ticketQuantities={ticketQuantities}
              setTicketQuantities={setTicketQuantities}
              namedPrices={namedPrices}
              baseTicketPriceCents={baseTicketPriceCents}
              ticketFeesCents={ticketFeesCents}
              totalAmountCents={totalAmountCents}
              discountedTicketPriceCents={discountedTicketPriceCents}
              numTicketsUserInGroups={numTicketsUserInGroups}
              disabled={disabled}
              updatePromoCode={updatePromoCode}
              updateTixId={updateTixId}
              setIsRedeemFreePromoCode={setIsRedeemFreePromoCode}
              showTipSection={showTipSection}
              tipAmountCents={tipAmountCents}
              setTipAmountCents={setTipAmountCents}
            />
          </div>
        </StandardBorderedContainer>
      ) : null}
      {verified ? paymentOrRsvpElement : null}
    </div>
  );
});
